import { useApolloClient, useMutation } from '@apollo/client/react';

import { useApolloError } from 'common/hooks/useApolloError';
import {
    IOfferEventSubscriptionStatus,
    CreateCertificateForEnrollmentsDocument,
    DeleteOfferEventEnrollmentsDocument,
    UpdateOfferEventEnrollmentsDocument,
} from 'graphql/types';

export const useEnrollmentsManagementMutations = () => {
    const { showApolloError } = useApolloError();
    const client = useApolloClient();
    const [updateEnrollment, { loading: updateEnrollmentLoading }] =
        useMutation(UpdateOfferEventEnrollmentsDocument);
    const [deleteEnrollments, { loading: deleteEnrollmentsLoading }] =
        useMutation(DeleteOfferEventEnrollmentsDocument);
    const [createCertificates, { loading: createCertificatesLoading }] =
        useMutation(CreateCertificateForEnrollmentsDocument);

    const evictEnrollmentsFromCache = (skipForEvent?: boolean) => {
        client.cache.evict({
            fieldName: 'manageableOfferEventEnrollments',
        });

        if (!skipForEvent) {
            client.cache.evict({
                fieldName: 'manageableOfferEventEnrollmentsForEvent',
            });
        }

        client.cache.evict({
            fieldName: 'paginatedOfferEvents',
        });

        client.cache.gc();
    };

    const handleUpdateEnrollments = async (
        enrollmentIds: string[],
        status: IOfferEventSubscriptionStatus,
        reason: string,
        generateCertificate?: boolean
    ) => {
        try {
            await updateEnrollment({
                variables: {
                    offerEventEnrollmentIds: enrollmentIds,
                    status,
                    reason,
                },
            });

            if (generateCertificate) {
                await createCertificates({
                    variables: {
                        offerEventEnrollmentIds: enrollmentIds,
                    },
                });
            }
        } catch (error) {
            showApolloError(error);

            return;
        }

        evictEnrollmentsFromCache(true);

        return;
    };

    const handleDeleteEnrollments = async (enrollmentIds: string[]) => {
        try {
            await deleteEnrollments({
                variables: {
                    offerEventEnrollmentIds: enrollmentIds,
                },
            });
        } catch (error) {
            showApolloError(error);

            return;
        }

        evictEnrollmentsFromCache();

        return;
    };

    return {
        onUpdateEnrollments: handleUpdateEnrollments,
        onDeleteEnrollments: handleDeleteEnrollments,
        loading:
            deleteEnrollmentsLoading ||
            createCertificatesLoading ||
            updateEnrollmentLoading,
    };
};
